
import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Node } from '@xyflow/react';
import { NodeData } from './initial-nodes';

interface GroupPropertiesPanelProps {
  node: Node<NodeData> | null;
  onClose: () => void;
  onSave: (nodeId: string, title: string, backgroundColor?: string) => void;
}

export function GroupPropertiesPanel({ node, onClose, onSave }: GroupPropertiesPanelProps) {
  const [title, setTitle] = useState('');
  const [backgroundColor, setBackgroundColor] = useState<string>('');

  const colorOptions = [
    { value: '', label: 'Sem cor', color: 'transparent' },
    { value: '#E3F2FD', label: 'Azul claro', color: '#E3F2FD' },
    { value: '#E8F5E8', label: 'Verde claro', color: '#E8F5E8' },
    { value: '#FFF9C4', label: 'Amarelo claro', color: '#FFF9C4' },
    { value: '#FCE4EC', label: 'Rosa claro', color: '#FCE4EC' },
    { value: '#FFF3E0', label: 'Laranja claro', color: '#FFF3E0' },
    { value: '#F3E5F5', label: 'Roxo claro', color: '#F3E5F5' },
  ];

  useEffect(() => {
    if (node) {
      setTitle(node.data.title || node.data.label || '');
      setBackgroundColor(node.data.backgroundColor || '');
    }
  }, [node]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (node) {
      onSave(node.id, title, backgroundColor);
    }
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  };

  if (!node) return null;

  return (
    <div className="fixed right-0 top-0 h-full w-80 bg-white border-l border-gray-200 shadow-lg flex flex-col z-20">
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <h3 className="text-lg font-medium">Propriedades da Moldura</h3>
        <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
          <X className="h-4 w-4" />
          <span className="sr-only">Fechar</span>
        </Button>
      </div>
      
      <form onSubmit={handleSubmit} className="flex-1 overflow-auto p-4 space-y-4">
        <div className="space-y-2">
          <Label htmlFor="group-id">ID</Label>
          <Input 
            id="group-id" 
            value={node.id || ''} 
            readOnly 
            disabled 
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="group-title">Título</Label>
          <Input 
            id="group-title" 
            name="title"
            value={title}
            onChange={handleTitleChange}
            placeholder="Digite o título da moldura"
          />
        </div>

        <div className="space-y-2">
          <Label>Cor de Fundo</Label>
          <div className="flex flex-wrap gap-2">
            {colorOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setBackgroundColor(option.value)}
                className={`w-8 h-8 border rounded transition-all ${
                  backgroundColor === option.value 
                    ? 'border-[#05445e] border-2' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                style={{
                  backgroundColor: option.color,
                  border: option.value === '' ? '2px dashed #ccc' : undefined
                }}
                title={option.label}
              />
            ))}
          </div>
        </div>
        
        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button 
            type="submit" 
            className="bg-[#05445e] hover:bg-[#189ab4]"
          >
            Salvar
          </Button>
        </div>
      </form>
    </div>
  );
}
